/**
 * Made-to-order product helpers (fulfillment_mode + lead time days).
 */

import { findProductInCatalog } from './catalog/findProduct';

/** @param {unknown} product */
export function isMadeToOrder(product) {
  return String(product?.fulfillment_mode || '')
    .trim()
    .toLowerCase() === 'made_to_order';
}

/** @param {unknown} value */
export function normalizeLeadDays(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseInt(String(value), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.min(365, n);
}

/**
 * @param {{ lead_time_min_days?: unknown, lead_time_max_days?: unknown }} product
 * @returns {{ min: number|null, max: number|null }}
 */
export function productLeadDays(product) {
  let min = normalizeLeadDays(product?.lead_time_min_days);
  let max = normalizeLeadDays(product?.lead_time_max_days);
  if (min != null && max != null && min > max) {
    const t = min;
    min = max;
    max = t;
  }
  if (min == null && max != null) min = max;
  if (max == null && min != null) max = min;
  return { min, max };
}

/**
 * @param {{ lead_time_min_days?: unknown, lead_time_max_days?: unknown }} product
 * @returns {string} e.g. "10–14 days" or "14 days"
 */
export function formatLeadTimeRange(product) {
  const { min, max } = productLeadDays(product);
  if (min == null && max == null) return '';
  if (min != null && max != null && min !== max) return `${min}–${max} days`;
  const days = max ?? min;
  return days === 1 ? '1 day' : `${days} days`;
}

/** Customer-facing PDP / checkout sentence for a single product. */
export function productMadeToOrderLeadNote(product) {
  if (!isMadeToOrder(product)) return '';
  const range = formatLeadTimeRange(product);
  if (range) {
    return `Made to order — typically ${range} before dispatch.`;
  }
  return 'Made to order — handmade after you order; takes longer than in-stock pieces.';
}

/**
 * Longest lead across cart lines (for checkout messaging).
 * @param {Array<{ categorySlug?: string, productId?: string }>} lines
 * @param {Record<string, unknown[]>|object} productsByCategory
 */
export function cartMadeToOrderLead(lines, productsByCategory) {
  let hasMto = false;
  let min = null;
  let max = null;
  for (const line of lines || []) {
    const product = findProductInCatalog(
      productsByCategory,
      line.categorySlug,
      line.productId
    );
    if (!isMadeToOrder(product)) continue;
    hasMto = true;
    const lead = productLeadDays(product);
    if (lead.min != null) min = min == null ? lead.min : Math.min(min, lead.min);
    if (lead.max != null) max = max == null ? lead.max : Math.max(max, lead.max);
  }
  return { hasMto, min, max, range: formatLeadTimeRange({ lead_time_min_days: min, lead_time_max_days: max }) };
}

/** Checkout / order-summary note when cart has MTO items. */
export function cartMadeToOrderNote(lines, productsByCategory) {
  const lead = cartMadeToOrderLead(lines, productsByCategory);
  if (!lead.hasMto) return '';
  if (lead.range) {
    return `Includes made-to-order pieces — typically ${lead.range} before dispatch after we start making your order.`;
  }
  return 'Includes made-to-order pieces — we start making them after your order; delivery takes longer.';
}

/**
 * Format a promised dispatch date for customers / admin.
 * @param {string|null|undefined} isoDate YYYY-MM-DD or datetime
 */
export function formatPromisedDispatchDate(isoDate) {
  const raw = String(isoDate || '').trim();
  if (!raw) return '';
  const d = new Date(raw.includes('T') || raw.includes(' ') ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Customer-facing ETA line from an order payload.
 * @param {{
 *   has_made_to_order?: boolean|number,
 *   production_started_at?: string|null,
 *   promised_dispatch_date?: string|null,
 *   lead_time_min_days?: number|null,
 *   lead_time_max_days?: number|null,
 *   status?: string,
 * }} order
 */
export function orderMadeToOrderEtaNote(order) {
  const hasMto = Boolean(Number(order?.has_made_to_order)) || Boolean(order?.has_made_to_order);
  if (!hasMto) return '';
  const status = String(order?.status || '').toLowerCase();
  if (['shipped', 'completed', 'cancelled', 'canceled'].includes(status)) return '';

  const promised = formatPromisedDispatchDate(order?.promised_dispatch_date);
  if (promised && order?.production_started_at) {
    return `Made to order — aiming to dispatch by ${promised}.`;
  }
  if (promised) {
    return `Made to order — target dispatch ${promised}.`;
  }
  const range = formatLeadTimeRange({
    lead_time_min_days: order?.lead_time_min_days,
    lead_time_max_days: order?.lead_time_max_days,
  });
  if (range) {
    return `Made to order — typically ${range} before dispatch once we start making it.`;
  }
  return 'Made to order — we will share a dispatch date when production starts.';
}
